/**
 * Track all initialized globals so that we can uninitialize them between tests to avoid cross-test
 * interference.
 */
let resetFuncs: Array<() => void> = [];

export function resetLazyGlobalsForTests(): void {
  const resetFuncsToCall = resetFuncs;
  resetFuncs = [];
  const errors: Array<unknown> = [];
  for (const reset of resetFuncsToCall) {
    try {
      reset();
      // Errors are collected and rethrown together below so that one failing dispose function cannot leave
      // unrelated globals initialized for the next test.
      // eslint-disable-next-line benchlint/always-throw-from-catch
    } catch (e) {
      errors.push(e);
    }
  }
  if (errors.length === 1) {
    throw errors[0];
  } else if (errors.length > 1) {
    throw new AggregateError(errors, 'Multiple errors while resetting lazy globals');
  }
}

/**
 * Helper to lazy-initialize a value at the top level and schedule it to be re-initialized between tests.
 *
 * This has a few benefits:
 * - Any top-level work depending on other modules might lead to circular import issues, which this avoids.
 * - Any top-level expensive work can slow down the application import process.
 * - Any top-level state should be reset between tests, which this does automatically.
 *
 * Example usage:
 * export const getFoo = lazyInit<Foo>(() => new Foo());
 *
 * If the initialized value owns anything outside of itself - a timer, an event listener, a subscription -
 * pass a `disposeFn` to release it. Dropping the reference is not enough: a live timer or listener keeps the
 * value reachable and running, so it would outlive the reset and interfere with later tests.
 *
 * Example usage:
 * export const getBar = lazyInit<Bar>(
 *   () => new Bar(),
 *   (bar) => bar.stop()
 * );
 */
export default function lazyInit<T>(initFn: () => T, disposeFn?: (value: T) => void): () => T {
  let value: T | null = null;
  let isInitialized = false;
  return () => {
    if (!isInitialized) {
      value = initFn();
      isInitialized = true;
      resetFuncs.push(() => {
        const initializedValue = value!;
        // Drop the reference to avoid possible GC issues.
        value = null;
        isInitialized = false;
        disposeFn?.(initializedValue);
      });
    }
    return value!;
  };
}
